/* eslint-disable react/prop-types */
import {
  Button,
  Card,
  Checkbox,
  Col,
  Form,
  Input,
  message,
  Row,
  Spin,
  Tooltip,
  Typography,
} from 'antd';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import Table from 'components/Table';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { ESTADOS_GLOBALES_MAP, isMutable } from 'utils/estadosGlobales';
import { onError } from 'utils/handlers';

import { InfoCircleOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { getGrupos } from 'api/catalogos/grupos';
import { getActuacionesPae } from 'api/pae';
import {
  deleteEstatusDeEjecucion,
  getEstatusDeEjecuciones,
  patchEstatusDeEjecucion,
  postEstatusDeEjecucion,
} from 'api/pae/estatusDeEjecuciones';
import { getOrigenesDeCredito } from 'api/pae/origenesDeCredito';
import { getTiposDeGastos } from 'api/recaudacion/tiposDeGasto';
import NumericInput from 'components/NumericInput';
import PlainTransfer from 'components/PlainTransfer';
import PropTypes from 'prop-types';
import keyGenerator from 'utils/keyGenerator';
import { capitalize } from 'utils/normalizers';
import ModalAyudaEstatus from './components/ModalAyudaEstatus';

export const permissionConfig = {
  permissionModel: 'estatudeejecucionpae',
  catalogsModels: [
    'actuacionpae',
    'tipodegasto',
    'tipodecargo',
    'origendelcreditopae',
    'group',
  ],
};

const FormSection = styled(Row).attrs({
  gutter: [10, 10],
})`
`;

const StyledCheckboxGroup = styled(Checkbox.Group)`
  width: 100%;
  margin-bottom: 10px;
  .ant-checkbox-wrapper {
    margin-bottom: 10px;
  }
`;

// Componente para renderizar las opciones del grupo de checkboxes
const CheckboxOptions = ({ checkboxOptions }) => (
  <Row>
    {checkboxOptions.map(({
      label,
      value,
      disabled,
      tooltip,
    }) => (
      <Col xs={24} sm={24} md={8} key={value}>
        <Checkbox value={value} disabled={disabled}>
          <Typography.Text>
            {label}
            {tooltip && (
              <Tooltip placement="top" title={tooltip}>
                <InfoCircleOutlined style={{ marginLeft: 4, color: 'gray' }} />
              </Tooltip>
            )}
          </Typography.Text>
        </Checkbox>
      </Col>
    ))}
  </Row>
);

CheckboxOptions.propTypes = {
  checkboxOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      disabled: PropTypes.bool,
    }),
  ).isRequired,
};

const EstatusDeEjecuciones = ({ permission }) => {
  const { estadosGlobales, actuacionesPae } = useSelector((state) => state.catalogs);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [mutable, setMutable] = useState(true);
  const [tiposDeGasto, setTiposDeGasto] = useState([]);
  const [roles, setRoles] = useState([]);
  const [origenesDeCredito, setOrigenesDeCredito] = useState([]);
  // const [tiposDeCargo, setTiposDeCargo] = useState({ results: [], count: 0 });
  const [checkedValues, setCheckedValues] = useState([]);
  const [visibleModalAyudaEstatus, setVisibleModalAyudaEstatus] = useState(false);

  const [form] = Form.useForm();

  const options = [
    // === CONFIGURACIÓN DE DOCUMENTOS Y REPORTES ===
    {
      label: 'Adjunta documentos',
      value: 'adjunta_documentos',
    },
    {
      label: 'Imprime reportes',
      value: 'imprime_reportes',
    },
    {
      label: 'Incluir reporte en archivo editable',
      value: 'incluir_reporte_en_archivo_editable',
      disabled: !checkedValues.includes('imprime_reportes'),
      tooltip: 'Para habilitar esta opción, primero debe activar "Imprime reportes".',
    },

    // === CONFIGURACIÓN DE VENCIMIENTOS ===
    {
      label: 'Tiene vencimiento',
      value: 'tiene_vencimiento',
    },
    {
      label: 'Vence en días hábiles',
      value: 'vence_en_dias_habiles',
    },

    // === CONFIGURACIÓN DE NOTIFICACIONES ===
    {
      label: 'Envía correo electrónico',
      value: 'envia_correo_electronico',
    },
    {
      label: 'Envía mensaje de SMS',
      value: 'envia_mensaje_eletronico_sms',
    },

    // === CONFIGURACIÓN DE EJECUTORES ===
    {
      label: 'Asigna ejecutor iniciando',
      value: 'asigna_ejecutor_iniciando_estado',
    },
    // Se quitó por peticion de Arreola
    // {
    //   label: 'Asigna ejecutor finalizando',
    //   value: 'asigna_ejecutor_finalizando_estado',
    // },
    {
      label: 'Cobros relacionados a ejecutor',
      value: 'cobros_relacionados_a_ejecutor',
    },

    // === CONFIGURACIÓN DE GASTOS Y CARTERA ===
    {
      label: 'Agrega gastos a cartera',
      value: 'agrega_gastos_a_cartera',
    },
    {
      label: 'Cancelar gastos activos al revertir',
      value: 'cancelar_gastos_activos_al_revertir',
    },
    {
      label: 'Impide cambiar cartera ejecutada',
      value: 'impide_cambiar_cartera_ejecutada',
    },

    // === CONFIGURACIÓN DE ESTADOS ESPECIALES ===
    {
      label: 'Es estatus único inicial',
      value: 'es_estatus_unico_inicial',
    },
    {
      label: 'Es estatus único de pago',
      value: 'es_estatus_unico_de_pago',
    },
    {
      label: 'Es estatus único de cancelación de pago',
      value: 'es_estatus_unico_de_cancelacion_de_pago',
    },
    {
      label: 'Es estatus único de cancelación de reporte moroso',
      value: 'es_estatus_unico_de_cancelacion_de_ejecucion',
    },
    {
      label: 'Es estatus final',
      value: 'es_estatus_final',
      disabled: checkedValues.includes('es_estatus_unico_de_cancelacion_de_ejecucion'),
      tooltip: 'Este check es obligatorio cuando se selecciona estatus único de cancelación de reporte moroso.',
    },

    // === CONFIGURACIÓN DE CRÉDITOS Y USO ===
    {
      label: 'Asigna crédito fiscal',
      value: 'asigna_credito_fiscal',
    },
    {
      label: 'Solo uso interno',
      value: 'solo_uso_interno',
      tooltip: 'Este check es obligatorio cuando se selecciona estatus único de cancelación de pago, pago o inicial.',
      disabled: checkedValues.includes('es_estatus_unico_de_cancelacion_de_pago')
        || checkedValues.includes('es_estatus_unico_de_pago')
        || checkedValues.includes('es_estatus_unico_inicial'),
    },
    {
      label: 'Adjuntar excel de ejecuciones a correo',
      value: 'envia_excel_al_correo',
      tooltip: 'Este check es solo se puede seleccionar cuando se selecciona enviar correo electrónico.',
      disabled: !checkedValues.includes('envia_correo_electronico'),
    },
  ];

  const fetchData = async () => {
    setLoading(true);
    const res = await getEstatusDeEjecuciones(null, false);
    setData(res);
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;

    const fetchAll = async () => {
      setLoading(true);
      const [
        _data,
        _tiposDeGasto,
        _roles,
        _origenesDeCredito,
      ] = await Promise.all([
        getEstatusDeEjecuciones(),
        getTiposDeGastos(),
        getGrupos(),
        getOrigenesDeCredito(),
        getActuacionesPae(),
      ]);
      if (mounted) {
        setData(_data);
        setTiposDeGasto(_tiposDeGasto);
        setRoles(_roles);
        setOrigenesDeCredito(_origenesDeCredito
          .filter((e) => e.estados_globales !== ESTADOS_GLOBALES_MAP.CANCELADO));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setVisible(false);
    setSelectedRowKeys([]);
    setCheckedValues([]);
    form.resetFields();
    setMutable(true);
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();

      options.forEach((item) => {
        values[item.value] = !!checkedValues.find((i) => i === item.value);
      });

      values.roles_que_puedan_aplicar = values?.roles_que_puedan_aplicar ?? [];

      const [key] = selectedRowKeys;

      // Configurar estatus final si es cancelación de reporte moroso
      if (values.es_estatus_unico_de_cancelacion_de_ejecucion) {
        values.es_estatus_final = true;
      }

      // Limpiar estatus siguientes si es estatus final
      if (values.es_estatus_final) {
        values.estatus_unico_siguiente_de_ejecucion = undefined;
        values.estatus_de_ejecucion_siguientes = [];
      }

      // Configurar estatus especiales (solo uso interno)
      const estatusEspeciales = [
        'es_estatus_unico_de_cancelacion_de_pago',
        'es_estatus_unico_de_pago',
        'es_estatus_unico_inicial',
      ];

      const esEstatusEspecial = estatusEspeciales
        .some((estatus) => values[estatus]);

      if (esEstatusEspecial && !values.es_estatus_unico_inicial) {
        values.solo_uso_interno = true;
        values.roles_que_puedan_aplicar = [];
        values.estatus_unico_siguiente_de_ejecucion = undefined;
      }

      if (values.estatus_unico_siguiente_de_ejecucion && !values.estatus_de_ejecucion_siguientes
        .includes(values.estatus_unico_siguiente_de_ejecucion)) {
        const sig = data.find((e) => e.id === values.estatus_unico_siguiente_de_ejecucion);
        message.warn(`El estatus ${sig?.descripcion} de reporte moroso no está incluido en los estatus aplicables de reporte moroso. Por favor, agréguelo y vuelva a intentar.`);
        return;
      }

      const saved = await (key ? patchEstatusDeEjecucion(key, values)
        : postEstatusDeEjecucion(values));
      if (saved) {
        onCancel();
        await fetchData();
      }
    } catch (err) {
      onError(err, setLoading);
    } finally {
      setLoading(false);
    }
  };

  const deleteItem = async () => {
    setLoading(true);
    if (selectedRowKeys.length) {
      const [key] = selectedRowKeys;
      const deleted = await deleteEstatusDeEjecucion(key);
      if (deleted) {
        setVisibleAlert(false);
        onCancel();
        await fetchData();
      }
    }
    setLoading(false);
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
    setMutable(isMutable(record));
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    keyGenerator(data, form);
    setVisible(true);
  };

  const onClickEdit = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      const record = data.find((e) => e.id === key);
      // const origen = origenesDeCredito.find((e) => e.id === record.origen_del_credito);
      // const _tiposDeCargo = await getTiposDeCargos({ sistema: origen.sistema });
      const checked = options.filter((e) => record[e.value]).map((e) => e.value);
      setCheckedValues(checked);
      // setTiposDeCargo(_tiposDeCargo);
      setVisible(true);
    } catch (err) {
      onError(err, setLoading);
    } finally {
      setLoading(false);
    }
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const columns = [
    {
      titleText: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 100,
      align: 'center',
    },
    {
      titleText: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 300,
      ellipsis: true,
      render: (text) => (
        <Typography.Text ellipsis={{ tooltip: text }}>
          {text}
        </Typography.Text>
      ),
    },
    {
      titleText: 'Origen de Crédito',
      dataIndex: 'origen_del_credito',
      key: 'origen_del_credito',
      width: 200,
      render: (text) => {
        const origen = origenesDeCredito.find((oc) => oc.id === text);
        return (
          <Typography.Text ellipsis={{ tooltip: origen?.descripcion }}>
            {origen?.descripcion || '-'}
          </Typography.Text>
        );
      },
    },
    {
      titleText: 'Actuación',
      dataIndex: 'actuacion',
      key: 'actuacion',
      width: 150,
      render: (text) => {
        const actuacion = actuacionesPae.find((ap) => ap.id === text);
        return (
          <Typography.Text ellipsis={{ tooltip: actuacion?.descripcion }}>
            {actuacion?.descripcion || '-'}
          </Typography.Text>
        );
      },
    },
    {
      titleText: 'Orden',
      dataIndex: 'orden_de_uso',
      key: 'orden_de_uso',
      width: 80,
      align: 'center',
      render: (text) => text,
    },
    {
      titleText: 'Adjunta Doc.',
      dataIndex: 'adjunta_documentos',
      key: 'adjunta_documentos',
      width: 150,
      align: 'center',
      render: (value) => (
        <Typography.Text type={value ? 'success' : 'danger'}>
          {value ? 'Sí' : 'No'}
        </Typography.Text>
      ),
    },
    {
      titleText: 'Tiene Vencimiento',
      dataIndex: 'tiene_vencimiento',
      key: 'tiene_vencimiento',
      width: 200,
      align: 'center',
      render: (value) => (
        <Typography.Text type={value ? 'success' : 'danger'}>
          {value ? 'Sí' : 'No'}
        </Typography.Text>
      ),
    },
    {
      titleText: 'Imprime Reportes',
      dataIndex: 'imprime_reportes',
      key: 'imprime_reportes',
      width: 200,
      align: 'center',
      render: (value) => (
        <Typography.Text type={value ? 'success' : 'danger'}>
          {value ? 'Sí' : 'No'}
        </Typography.Text>
      ),
    },
    {
      titleText: 'Envía Correo',
      dataIndex: 'envia_correo_electronico',
      key: 'envia_correo_electronico',
      width: 150,
      align: 'center',
      render: (value) => (
        <Typography.Text type={value ? 'success' : 'danger'}>
          {value ? 'Sí' : 'No'}
        </Typography.Text>
      ),
    },
    {
      titleText: 'Envía SMS',
      dataIndex: 'envia_mensaje_eletronico_sms',
      key: 'envia_mensaje_eletronico_sms',
      width: 150,
      align: 'center',
      render: (value) => (
        <Typography.Text type={value ? 'success' : 'danger'}>
          {value ? 'Sí' : 'No'}
        </Typography.Text>
      ),
    },
    {
      titleText: 'Asigna Ejecutor Iniciando',
      dataIndex: 'asigna_ejecutor_iniciando_estado',
      key: 'asigna_ejecutor_iniciando_estado',
      width: 200,
      align: 'center',
      render: (value) => (
        <Typography.Text type={value ? 'success' : 'danger'}>
          {value ? 'Sí' : 'No'}
        </Typography.Text>
      ),
    },
    {
      titleText: 'Asigna Ejecutor Finalizando',
      dataIndex: 'asigna_ejecutor_finalizando_estado',
      key: 'asigna_ejecutor_finalizando_estado',
      width: 250,
      align: 'center',
      render: (value) => (
        <Typography.Text type={value ? 'success' : 'danger'}>
          {value ? 'Sí' : 'No'}
        </Typography.Text>
      ),
    },
    {
      titleText: 'Cobros Relacionados a Ejecutor',
      dataIndex: 'cobros_relacionados_a_ejecutor',
      key: 'cobros_relacionados_a_ejecutor',
      width: 250,
      align: 'center',
      render: (value) => (
        <Typography.Text type={value ? 'success' : 'danger'}>
          {value ? 'Sí' : 'No'}
        </Typography.Text>
      ),
    },
    {
      titleText: 'Agrega Cobros a Cartera',
      dataIndex: 'agrega_gastos_a_cartera',
      key: 'agrega_gastos_a_cartera',
      width: 200,
      align: 'center',
      render: (value) => (
        <Typography.Text type={value ? 'success' : 'danger'}>
          {value ? 'Sí' : 'No'}
        </Typography.Text>
      ),
    },
    {
      titleText: 'Cancelar Cobros Activos',
      dataIndex: 'cancelar_gastos_activos_al_revertir',
      key: 'cancelar_gastos_activos_al_revertir',
      width: 200,
      align: 'center',
      render: (value) => (
        <Typography.Text type={value ? 'success' : 'danger'}>
          {value ? 'Sí' : 'No'}
        </Typography.Text>
      ),
    },
    {
      titleText: 'Fundamento Legal',
      dataIndex: 'fundamento_legal',
      key: 'fundamento_legal',
      width: 200,
      ellipsis: true,
      render: (text) => (
        <Typography.Text ellipsis={{ tooltip: text }}>
          {text || '-'}
        </Typography.Text>
      ),
    },
    {
      titleText: 'Observaciones',
      dataIndex: 'observaciones',
      key: 'observaciones',
      width: 200,
      ellipsis: true,
      render: (text) => (
        <Typography.Text ellipsis={{ tooltip: text, symbol: '...' }}>
          {text || '-'}
        </Typography.Text>
      ),
    },
    {
      titleText: 'Estado',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 120,
      render: (value) => {
        const estado = estadosGlobales.find((eg) => eg.id === value);
        return (
          <Typography.Text type={estado?.descripcion?.toLowerCase().includes('activo') ? 'success' : 'default'}>
            {estado?.descripcion || '-'}
          </Typography.Text>
        );
      },
    },
  ];

  const rules = {
    required: [{ required: mutable }],
  };

  // const onChangeOrigenDeCreditoOrAddCobrosACartera = async () => {
  //   try {
  //     const { origen_del_credito } = form.getFieldsValue();

  //     // Si no hay origen de crédito seleccionado y no se agregan cobros a cartera, no hacer nada
  //     if (!origen_del_credito && !checkedValues.includes('agrega_gastos_a_cartera')) {
  //       setTiposDeCargo({ results: [], count: 0 });
  //       return;
  //     }

  //     setLoading(true);

  //     // Buscar el origen de crédito seleccionado
  //     const origenSeleccionado = origenesDeCredito.find((o) => o.id === origen_del_credito);

  //     // Si no se encontró el origen o no tiene sistema, mostrar error
  //     if (!origenSeleccionado || !origenSeleccionado.sistema) {
  //       message.error('El origen de crédito no tiene sistema configurado');
  //       setTiposDeCargo({ results: [], count: 0 });
  //       return;
  //     }

  //     // Obtener tipos de cargo basados en el sistema del origen de crédito
  //     const _tiposDeCargo = await getTiposDeCargos({
  //       sistema: origenSeleccionado.sistema,
  //       padron: origenSeleccionado.padron,
  //     });
  //     setTiposDeCargo(_tiposDeCargo);
  //   } catch (error) {
  //     message.error('Error al cargar los tipos de cargo');
  //     setTiposDeCargo({ results: [], count: 0 });
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // const paginationCallbackTiposDeCargo = async (page = 1, q = null, params = null) => {
  //   try {
  //     setLoading(true);
  //     const { origen_del_credito } = form.getFieldsValue();

  //     if (!origen_del_credito) {
  //       message.warning('Debe seleccionar un origen de crédito');
  //       setTiposDeCargo({ results: [], count: 0 });
  //       return;
  //     }

  //     const origenSeleccionado = origenesDeCredito.find((o) => o.id === origen_del_credito);

  //     if (!origenSeleccionado || !origenSeleccionado.sistema) {
  //       message.error('El origen de crédito no tiene sistema configurado');
  //       setTiposDeCargo({ results: [], count: 0 });
  //       return;
  //     }

  //     const _tiposDeCargo = await getTiposDeCargos({
  //       page,
  //       sistema: origenSeleccionado.sistema,
  //       padron: origenSeleccionado.padron,
  //       descripcion: q,
  //       ...params,
  //     });

  //     const esMismaBusqueda = (tiposDeCargo.q && q && tiposDeCargo.q === q)
  //       || (!tiposDeCargo.q && !q);

  //     if (esMismaBusqueda && page > 1) {
  //       setTiposDeCargo((prev) => ({
  //         ..._tiposDeCargo,
  //         results: [...(prev?.results || []), ..._tiposDeCargo.results],
  //         q,
  //       }));
  //     } else {
  //       setTiposDeCargo({ ..._tiposDeCargo, q });
  //     }
  //   } catch (error) {
  //     message.error('Error al cargar los tipos de cargo');
  //     setTiposDeCargo({ results: [], count: 0 });
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const onChangeCheckedValues = (values) => {
    // Verificar si se selecciona solo uso interno manualmente
    const isSelectedSoloUsoInterno = values.includes('solo_uso_interno')
      && !checkedValues.includes('solo_uso_interno');

    // Verificar si se deselecciona solo uso interno manualmente
    const isDeselectedSoloUsoInterno = !values.includes('solo_uso_interno')
      && checkedValues.includes('solo_uso_interno');

    // Verificar si se selecciona estatus único de cancelación de pago
    const isSelectedEstatusUnicoDeCancelacionDePago = values.includes('es_estatus_unico_de_cancelacion_de_pago')
      && !checkedValues.includes('es_estatus_unico_de_cancelacion_de_pago');

    // Verificar si se selecciona estatus único de pago
    const isSelectedEstatusUnicoDePago = values.includes('es_estatus_unico_de_pago')
      && !checkedValues.includes('es_estatus_unico_de_pago');

    // Verificar si se selecciona estatus único inicial
    const isSelectedEstatusUnicoInicial = values.includes('es_estatus_unico_inicial')
      && !checkedValues.includes('es_estatus_unico_inicial');

    // Verificar si se selecciona agregar gastos a cartera
    // const isSelectedAgregarGastosACartera = values.includes('agrega_gastos_a_cartera')
    //   && !checkedValues.includes('agrega_gastos_a_cartera');

    // Verificar si se selecciona estatus final
    const isSelectedEsFinal = values.includes('es_estatus_final')
      && !checkedValues.includes('es_estatus_final');

    const isSelectedCancelacionDeEjecucion = values.includes('es_estatus_unico_de_cancelacion_de_ejecucion')
      && !checkedValues.includes('es_estatus_unico_de_cancelacion_de_ejecucion');

    const isDeselectedImprimeReportes = !values.includes('imprime_reportes')
      && checkedValues.includes('imprime_reportes');

    const isDeselectedEnviarCorreoElectronico = !values.includes('envia_correo_electronico')
      && checkedValues.includes('envia_correo_electronico');

    if (isDeselectedImprimeReportes) {
      const newValues = values.filter((v) => v !== 'incluir_reporte_en_archivo_editable');
      setCheckedValues(newValues);
      return;
    }

    if (isDeselectedEnviarCorreoElectronico) {
      const newValues = values.filter((v) => v !== 'envia_excel_al_correo');
      setCheckedValues(newValues);
      return;
    }

    // Si se selecciona estatus final, resetear campos relacionados
    if (isSelectedEsFinal) {
      form.resetFields(['estatus_unico_siguiente_de_ejecucion', 'estatus_de_ejecucion_siguientes']);
    }

    if (isSelectedCancelacionDeEjecucion) {
      const newValues = [...new Set([...values, 'es_estatus_final'])];
      setCheckedValues(newValues);
      return;
    }

    // Si se selecciona cualquier estatus único (cancelación de pago, pago, o inicial),
    // se debe resetear el campo de roles que pueden aplicar
    // y se debe agregar automáticamente solo uso interno
    if (isSelectedEstatusUnicoDeCancelacionDePago
      || isSelectedEstatusUnicoDePago
      || isSelectedEstatusUnicoInicial) {
      form.resetFields(['roles_que_puedan_aplicar', 'estatus_unico_siguiente_de_ejecucion']);
      // Agregar solo_uso_interno automáticamente
      const newValues = [...new Set([...values, 'solo_uso_interno'])];
      setCheckedValues(newValues);
      return;
    }

    // Si se deselecciona solo uso interno manualmente, se debe resetear el campo de estatus
    // único siguiente de ejecución
    if (isDeselectedSoloUsoInterno) {
      form.resetFields(['estatus_unico_siguiente_de_ejecucion']);
    }

    // Si se selecciona solo uso interno manualmente, se debe resetear el campo de estatus
    // único siguiente de ejecución
    if (isSelectedSoloUsoInterno) {
      form.resetFields(['estatus_unico_siguiente_de_ejecucion']);
    }

    // Si se selecciona agregar gastos a cartera, se deben obtener los tipos de gastos
    // if (isSelectedAgregarGastosACartera) {
    //   onChangeOrigenDeCreditoOrAddCobrosACartera();
    // }

    // Actualizar el estado normalmente
    setCheckedValues(values);
  };

  return (
    <Row align="center" justify="center" className="container">
      <Spin
        tip="Cargando..."
        spinning={loading}
      >
        {!visible && (
          <Table
            cols={columns}
            data={data}
            rowSelection={rowSelection}
            handleOnRowClick={handleOnRowClick}
            controls={{
              onClickAdd,
              onClickEdit,
              onClickDelete,
            }}
            rowKey="id"
            permission={permission}
            hasAllViewPermission
            allowColumnSelector={false}
            deleteText="Cancelar"
          />
        )}
        {visible && (
          <Card
            className={!mutable ? 'without-feedback' : 'form-container'}
            title={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Estatus de Reporte Moroso`}
            extra={(
              <FormSubmitControls
                onFinish={onFinish}
                onCancel={onCancel}
                loading={loading}
                mutable={mutable}
                permission={permission}
                selectedRowKeys={selectedRowKeys}
                callback={(id) => {
                  const [key] = selectedRowKeys;
                  const normalized = data.map((e) => {
                    if (e.id === key) {
                      const match = estadosGlobales.find((eg) => eg.id === id);
                      form.setFieldsValue({ estados_globales: match.id });
                      setMutable(true);
                      return { ...e, estados_globales: id };
                    }
                    return e;
                  });
                  setData(normalized);
                }}
              />
            )}
            bordered={false}
          >
            <Form
              layout="vertical"
              form={form}
              onFinish={onFinish}
              scrollToFirstError={{
                behavior: 'smooth',
                block: 'center',
              }}
              initialValues={{ estados_globales: 1 }}
            >
              <Row gutter={[10, 10]}>
                {!!selectedRowKeys.length && (
                  <Col xs={24} md={4}>
                    <Form.Item
                      name="id"
                      label="ID"
                    >
                      <Input disabled />
                    </Form.Item>
                  </Col>
                )}
                <Col
                  xs={24}
                  md={selectedRowKeys.length ? 12 : 16}
                >
                  <Form.Item
                    name="descripcion"
                    rules={rules.required}
                    label="Descripción"
                    normalize={capitalize}
                  >
                    <Input.TextArea
                      disabled={!mutable}
                      allowClear
                      maxLength={200}
                      autoSize
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="origen_del_credito"
                    rules={rules.required}
                    label="Origen de Crédito"
                  >
                    <Select
                      disabled={!mutable || selectedRowKeys.length}
                      dataSource={origenesDeCredito}
                      // onChange={onChangeOrigenDeCreditoOrAddCobrosACartera}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[10, 10]}>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    name="actuacion"
                    label="Actuación"
                    hasFeedback
                  >
                    <Select
                      dataSource={actuacionesPae}
                      allowClear
                      disabled={!mutable || selectedRowKeys.length}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <NumericInput
                    name="orden_de_uso"
                    label="Orden de uso"
                    disabled={!mutable}
                    rules={rules.required}
                    allowClear
                    allowZero
                  />
                </Col>
              </Row>
              <Row gutter={[10, 10]} justify="end">
                <Col xs={24} md={16}>
                  <Form.Item
                    name="fundamento_legal"
                    normalize={capitalize}
                    label="Fundamento Legal"
                    hasFeedback
                  >
                    <Input.TextArea
                      allowClear
                      disabled={!mutable}
                      autoSize={{ minRows: 3, maxRows: 6 }}
                      maxLength={500}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <StyledCheckboxGroup
                value={checkedValues}
                disabled={!mutable}
                onChange={onChangeCheckedValues}
              >
                <CheckboxOptions checkboxOptions={options} />
              </StyledCheckboxGroup>
              <FormSection>
                {checkedValues.includes('tiene_vencimiento') && (
                  <Col xs={24} sm={12} md={8}>
                    <NumericInput
                      name="numero_de_dias_de_vencimiento"
                      label="Días de Vencimiento"
                      rules={rules.required}
                      disabled={!mutable}
                      allowClear
                    />
                  </Col>
                )}
              </FormSection>
              <FormSection>
                {checkedValues.includes('imprime_reportes') && (
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item
                      name="reportes_id_a_imprimir"
                      label="Reportes a imprimir"
                      hasFeedback
                    >
                      <Input
                        allowClear
                        disabled={!mutable}
                      />
                    </Form.Item>
                  </Col>
                )}
              </FormSection>

              {(checkedValues.includes('envia_correo_electronico')
              || checkedValues.includes('envia_mensaje_eletronico_sms')) && (
              <Row justify="end">
                <Col>
                  <Button
                    onClick={() => setVisibleModalAyudaEstatus(true)}
                    type="link"
                    icon={<QuestionCircleOutlined />}
                  >
                    Ayuda para correo electrónico o mensaje SMS
                  </Button>
                </Col>
              </Row>
              )}

              <FormSection justify="end">
                {checkedValues.includes('envia_correo_electronico') && (
                <>
                  <Col xs={24} md={16}>
                    <Form.Item
                      name="titulo_de_correo"
                      label="Título del correo"
                      hasFeedback
                      rules={rules.required}
                    >
                      <Input
                        allowClear
                        disabled={!mutable}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={16}>
                    <Form.Item
                      name="contenido_de_correo"
                      label="Mensaje del correo (contenido)"
                      rules={rules.required}
                    >
                      <Input.TextArea
                        allowClear
                        disabled={!mutable}
                        autoSize={{ minRows: 6, maxRows: 6 }}
                      />
                    </Form.Item>
                  </Col>
                </>
                )}
              </FormSection>

              <FormSection justify="end">
                {checkedValues.includes('envia_mensaje_eletronico_sms') && (
                <Col xs={24} md={16}>
                  <Form.Item
                    name="contenido_de_mensaje"
                    label="Mensaje del SMS"
                    rules={rules.required}
                  >
                    <Input.TextArea
                      allowClear
                      disabled={!mutable}
                      autoSize={{ minRows: 6, maxRows: 6 }}
                    />
                  </Form.Item>
                </Col>
                )}
              </FormSection>

              <FormSection>
                {checkedValues.includes('agrega_gastos_a_cartera') && (
                  <>
                    {/* <Col span={24}>
                      <PlainTransfer
                        dataSource={tiposDeCargo}
                        label="Tipos de cargo"
                        formItemName="tipo_de_cargo"
                        form={form}
                        filterProp="descripcion"
                        disabled={!mutable}
                        callbackPagination={paginationCallbackTiposDeCargo}
                        pagination={false}
                      />
                    </Col> */}
                    <Col span={24}>
                      <PlainTransfer
                        dataSource={tiposDeGasto.filter((tg) => {
                          const origen = origenesDeCredito
                            .find((oc) => oc.id === form.getFieldValue('origen_del_credito'));
                          return tg.padron === origen?.padron;
                        })}
                        label="Tipo de gastos"
                        formItemName="tipo_de_gasto"
                        form={form}
                        filterProp="descripcion"
                        disabled={!mutable}
                      />
                    </Col>
                  </>
                )}
              </FormSection>
              <FormSection>
                <Col span={24}>
                  <PlainTransfer
                    dataSource={roles}
                    label="Roles que pueden aplicar"
                    formItemName="roles_que_puedan_aplicar"
                    form={form}
                    disabled={!mutable
                      || checkedValues.includes('es_estatus_unico_de_cancelacion_de_pago')
                      || checkedValues.includes('es_estatus_unico_de_pago')}
                    filterProp="name"
                  />
                  {(!checkedValues.includes('solo_uso_interno')
                  || checkedValues.includes('es_estatus_unico_inicial')) && (
                    <>
                      <Form.Item
                        name="estatus_unico_siguiente_de_ejecucion"
                        label="Estatus único siguiente de reporte moroso"
                      >
                        <Select
                          dataSource={data}
                          allowClear
                          disabled={!mutable || checkedValues.includes('es_estatus_final')}
                        />
                      </Form.Item>
                      <PlainTransfer
                        rules={[
                          {
                            async validator(_, value) {
                              const statusSiguiente = form.getFieldValue('estatus_unico_siguiente_de_ejecucion');
                              if (statusSiguiente && !value.length) {
                                throw new Error('El campo es requerido');
                              }
                            },
                          },
                        ]}
                        dataSource={data}
                        label="Estatus aplicables de reporte moroso"
                        formItemName="estatus_de_ejecucion_siguientes"
                        form={form}
                        filterProp="descripcion"
                        disabled={!mutable || checkedValues.includes('es_estatus_final')}
                      />
                    </>
                  )}
                </Col>
              </FormSection>
              <br />
              <Row gutter={[10, 10]}>
                <Col xs={24} sm={16} md={16}>
                  <Form.Item
                    name="observaciones"
                    label="Observaciones"
                    normalize={capitalize}
                    hasFeedback
                  >
                    <Input.TextArea
                      allowClear
                      disabled={!mutable}
                      autoSize={{ minRows: 3, maxRows: 6 }}
                      maxLength={500}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="estados_globales"
                    label="Estado"
                    hasFeedback
                  >
                    <Select
                      dataSource={estadosGlobales}
                      allowClear
                      disabled
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item hidden>
                <Button htmlType="submit" />
              </Form.Item>
            </Form>
          </Card>
        )}

        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => {
            setVisibleAlert(false);
            onCancel();
          }}
          visible={visibleAlert}
          content={` el estatus de reporte moroso ${form.getFieldValue('descripcion')}`}
          loading={loading}
        />
      </Spin>

      <ModalAyudaEstatus
        open={visibleModalAyudaEstatus}
        onCancel={() => setVisibleModalAyudaEstatus(false)}
      />
    </Row>
  );
};

export default EstatusDeEjecuciones;
